import { useState, useEffect, useCallback } from 'react';
import { getChildChapters, getChapterPrompts } from '../services/childChaptersAPI';

/**
 * Hook for fetching and managing a child's chapters and their prompts.
 * @param {string} memberId - The family member (person) UUID
 */
export function useChildChapters(memberId) {
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchChapters = useCallback(async () => {
    if (!memberId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getChildChapters(memberId);
      // Normalise — API may return { results: [...] } or a plain array
      setChapters(Array.isArray(data) ? data : data.results || []);
    } catch (err) {
      console.error('useChildChapters: Error fetching chapters:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => {
    fetchChapters();
  }, [fetchChapters]);

  /**
   * Fetch prompts for a specific chapter.
   * Returns the prompts array directly.
   */
  const fetchPrompts = useCallback(async (bandKey, chapterIndex) => {
    try {
      const data = await getChapterPrompts(memberId, bandKey, chapterIndex);
      return Array.isArray(data) ? data : data.results || [];
    } catch (err) {
      console.error('useChildChapters: Error fetching prompts:', err);
      return [];
    }
  }, [memberId]);

  return {
    chapters,
    loading,
    error,
    refetch: fetchChapters,
    fetchPrompts,
  };
}
